import { useMemo, useState } from "react";
import styled from "styled-components/macro";
import { DocumentPreview } from "../../pages/admin/SellerAdmin/PIM/SellarAdminPIMAssets/util/DocumentPreview";
import { AddOrEditImageAttribute } from "../../pages/admin/SellerAdmin/PIM/SellerAdminPIMAttributes/ImageAttributes/AddOrEditImageAttribute";
import type {
  Assets,
  AttributeSchema,
  ImageAttributeValue,
} from "../../types/types.PIM";
import { useTranslation } from "react-i18next";
import { useAuthContext } from "../Auth";
import { EditButton } from "../Buttons/Buttons";
import { SlideOut } from "../SlideOut/SlideOut";
import { spacingTokens } from "../../designTokens";

const ImageCaptionContainer = styled.div<{
  size: ImageAttributeValue["image_dimensions"];
}>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
  align-items: center;
  width: ${({ size }) =>
    size === "small" ? "30%" : size === "medium" ? "50%" : "100%"};
  img {
    width: 100%;
    height: auto;
    cursor: pointer;
  }
  p {
    margin: 0;
  }
  cursor: pointer;
`;

export const ImageAttributePreview = ({
  asset,
  attribute,
  onCreateImageAttribute,
  image_attribute_value,
  is_portfolio_page = false,
}: {
  asset: Assets;
  attribute: AttributeSchema;
  onCreateImageAttribute?: (
    image_arr: ImageAttributeValue | null,
    attribute: AttributeSchema
  ) => void;
  image_attribute_value?: ImageAttributeValue;
  is_portfolio_page?: boolean;
}) => {
  const { t } = useTranslation();
  const [show_preview, set_show_preview] = useState(false);
  const [show_edit_slideout, set_show_edit_slideout] = useState(false);
  const { roleIsGuest, roleIsSomeKindOfBuyer } = useAuthContext();

  const image_url = useMemo(() => {
    return attribute?.values?.[0]?.cdn_url ?? asset.signed_url;
  }, [asset.signed_url, attribute?.values]);

  const onEdit = () => {
    set_show_edit_slideout(true);
  };

  const onDownload = () => {
    if (image_url.includes("https")) {
      // create an anchor tag and click it to download the image
      const image_download_elem = document.createElement("a");
      image_download_elem.href = image_url;
      image_download_elem.download = asset.name;
      image_download_elem.rel = "noreferrer";
      image_download_elem.target = "_blank";
      image_download_elem.click();
    } else {
      window.open(image_url, "_blank");
    }
  };

  const onEditComplete = (image_attribute: ImageAttributeValue | null) => {
    set_show_edit_slideout(false);
    set_show_preview(false);
    onCreateImageAttribute?.(image_attribute, attribute);
  };

  const asset_to_preview = {
    ...asset,
    can_edit: !is_portfolio_page && !!onCreateImageAttribute,
    can_change_visibility: false,
    can_delete: false,
    can_external_download: roleIsGuest ? asset.visibility === "public" : true,
    can_view_details: !is_portfolio_page,
  };

  const caption =
    image_attribute_value?.caption ??
    (attribute?.values?.[0]?.value as ImageAttributeValue)?.caption ??
    "";
  const size =
    image_attribute_value?.image_dimensions ??
    (attribute?.values?.[0]?.value as ImageAttributeValue)?.image_dimensions ??
    "small";

  const is_visible = roleIsGuest
    ? asset.visibility === "public"
    : roleIsSomeKindOfBuyer
    ? asset.visibility !== "internal"
    : true;

  return is_visible ? (
    <div style={{ display: "flex", gap: spacingTokens.spacing_md }}>
      <ImageCaptionContainer
        size={size}
        onClick={() => {
          set_show_preview(true);
        }}
      >
        <img src={image_url} alt={caption} />
        {caption && <p>{caption}</p>}
      </ImageCaptionContainer>
      {show_preview && (
        <DocumentPreview
          assets={[asset_to_preview]}
          selected_asset_idx={0}
          onCancelPreview={() => set_show_preview(false)}
          onEdit={asset_to_preview.can_edit ? onEdit : undefined}
          onDownload={
            asset_to_preview.can_external_download ? onDownload : undefined
          }
        />
      )}
      <SlideOut
        title={t("Edit Image")}
        show={show_edit_slideout}
        closeFlyout={() => set_show_edit_slideout(false)}
      >
        <AddOrEditImageAttribute
          attribute={attribute}
          onComplete={onEditComplete}
          onCancel={() => set_show_edit_slideout(false)}
          existing_asset={asset}
          image_attribute_value={image_attribute_value}
        />
      </SlideOut>
      {asset_to_preview.can_edit && (
        <EditButton type="button" onClick={onEdit} />
      )}
    </div>
  ) : (
    <></>
  );
};
